import * as H from 'history'
import { RouteNames } from 'RouteNames'
import { useHistory } from 'react-router-dom'
import qs from 'query-string'
import { v4 as uuidv4 } from 'uuid'
import { getPaymentSuccessUrl } from 'Lib/EventsUtils'
import { isAndroid } from 'Lib/UIUtils'
import { isProductionHost, isStagingHost } from 'Lib/hosts'

/** the next line are executed in global scope at startup */
const INITIAL_SEARCH_PATH = location.search
// strict null check -> empty string is also valid original search params
if (localStorage.getItem('originalSearchParams') === null) {
  localStorage.setItem('originalSearchParams', INITIAL_SEARCH_PATH)
  // This is where the localStorage is populated for the first time. (Even if there is not search parameters)
  // We need to save the time of this first visit.
  localStorage.setItem('firstVisit', new Date().toISOString())
}

/** The query string the current page-load started with, captured before any
 *  redirect or sanitizeUrl strip. It's what makes click-IDs/lang survive
 *  onboarding's history.push. Exposed read-only for the dev tracking inspector. */
export const getInitialSearchPath = (): string => INITIAL_SEARCH_PATH

/**
 * Gets the current app environment based on which host is the app running
 * Unfortunately there is no other way to determine this, since firebase hosting does not provide environment variables
 */
export function getEnvironment() {
  if (isProductionHost()) {
    return 'production'
  }

  if (isStagingHost()) {
    return 'staging'
  }

  return 'development'
}

// Live URL merged over the all-time localStorage store (URL wins). Lets delayed
// readers (token login, payment flow) recover keys sanitizeUrl stripped from the
// URL but kept in storage.
// ⚠️ NEVER write this back to the URL — the store persists across sessions, so it
// would resurrect a previous visit's click-IDs. Use removeLiveUrlParams to strip.
export const getSearchParams = (): URLSearchParams => {
  const merged = new URLSearchParams(localStorage.getItem('searchParams') ?? '')
  new URLSearchParams(window.location.search).forEach((value, key) => merged.set(key, value))
  return merged
}

// The live address-bar query, nothing else — never the merged localStorage
// store. This is the read to use for anything that WRITES the URL back.
export const getLiveUrlParams = (): URLSearchParams =>
  new URLSearchParams(window.location.search)

// Drop `keysToRemove` from the LIVE URL only, returning the `search` string (no
// leading '?') for history.replace / put(replace). Unlike rewriting getSearchParams(),
// it touches only what's in the URL, so it can't resurrect the persisted store.
export const removeLiveUrlParams = (keysToRemove: string[]): string => {
  const params = getLiveUrlParams()
  keysToRemove.forEach(key => params.delete(key))
  return params.toString()
}

export const getLowercaseSearchParams = (): URLSearchParams => {
  const searchParams = getSanitizedMarketingSearchParams().entries()
  const lowercaseParams = Array.from(searchParams).map(([key, value]) => [key.toLowerCase(), value])
  return new URLSearchParams(lowercaseParams)
}

export const getCookiesMap = (): Map<string, string> => {
  const cookiesMap = new Map<string, string>()
  const cookiesArray = document.cookie.split(';')
  cookiesArray.forEach(cookieString => {
    // Only the first '=' separates: a base64 value carries its own padding, and
    // split('=', 2) would drop everything from the padding onwards.
    const separatorIndex = cookieString.indexOf('=')

    if (separatorIndex === -1) return

    cookiesMap.set(cookieString.slice(0, separatorIndex).trim(), cookieString.slice(separatorIndex + 1).trim())
  })
  return cookiesMap
}

export const getSearchParamsString = (): string => {
  const searchParams = getSearchParams()
  return searchParams.toString() ? `?${searchParams.toString()}` : ''
}

export const hasInAppBrowserAsUtmSource = () => {
  const searchParams = getSearchParams()
  return searchParams.get('utm_source') === 'in_app_browser' // Ensure that the utm_source value is matching with the value in ManageSubscriptionScreen.js
}

export const shouldAttemptToCloseInAppBrowser = () =>
  hasInAppBrowserAsUtmSource() && // Close feature is only working on the in app browsers
  !isAndroid() // Close feature is not working on Android for some reason. I couldn't check the root cause because of time restrictions. It must be something related to the deeplinks. See: https://bodyfastworkspace.slack.com/archives/C05E05DK2SY/p1726748368880109

// Query-string keys that mark a click as coming from a marketing channel. Used
// by getSanitizedMarketingSearchParams to pick the best attribution source.
// Verify additions against GA4 page_location before adding — most platform
// click-IDs for channels we don't run never actually arrive.
const AD_PARAM_KEYS = [
  // UTMs (channel-agnostic)
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  // Meta
  'fbclid',
  // Google — gclid (web), gbraid/wbraid (iOS / consent-restricted; gbraid alone
  // is ~the same order of magnitude as gclid in our GA4 data), dclid (Display)
  'gclid', 'gbraid', 'wbraid', 'dclid',
  // TikTok / X / Snap / NewsBreak
  'ttclid', 'twclid', 'sccid', 'nb_cid',
  // Microsoft / LinkedIn / Reddit / Pinterest — not observed in GA4 (90d) as of
  // 2026-06; added pre-emptively so they're recognized if those channels go live
  'msclkid', 'li_fat_id', 'rdt_cid', 'epik'
]

const containsAdParams = (urlSearchParams: URLSearchParams): boolean =>
  AD_PARAM_KEYS.some(key => urlSearchParams.has(key))

// Sensitive params that must NEVER persist in the URL — Stripe/payment params and
// auth tokens (token, futoken, ctoken). sanitizeUrl keeps every *other* param, so
// this list is the security boundary. Single source of truth: the dev-panel leak
// check imports it too. Add any new credential-like param here.
export const SENSITIVE_URL_PARAMS = [
  'payment_intent', 'payment_intent_client_secret', 'selectedPackageId',
  'subscriptionId', 'send_event', 'redirect_status',
  // Checkout session ids: one-shot purchase identifiers. Read them via
  // getSearchParams/getInitialSearchPath, never from the live URL, or sanitizeUrl
  // will have cleaned them before the reader runs.
  'session_id', 'ebook_session_id',
  'token', 'futoken', 'ctoken'
]

const sanitizeSearchParams = (urlSearchParams: URLSearchParams): URLSearchParams => {
  SENSITIVE_URL_PARAMS.forEach(param => urlSearchParams.delete(param))

  return urlSearchParams
}

export const getSanitizedMarketingSearchParams = (): URLSearchParams => {
  const urlSearchParams1 = new URLSearchParams(window.location.search)
  if (containsAdParams(urlSearchParams1)) {
    return sanitizeSearchParams(urlSearchParams1)
  }

  const urlSearchParams2 = new URLSearchParams(INITIAL_SEARCH_PATH ?? '')
  if (containsAdParams(urlSearchParams2)) {
    return sanitizeSearchParams(urlSearchParams2)
  }

  const urlSearchParams3 = new URLSearchParams(localStorage.getItem('searchParams') ?? '')
  if (containsAdParams(urlSearchParams3)) {
    return sanitizeSearchParams(urlSearchParams3)
  }

  const urlSearchParams4 = new URLSearchParams(localStorage.getItem('originalSearchParams') ?? '')
  if (containsAdParams(urlSearchParams4)) {
    return sanitizeSearchParams(urlSearchParams4)
  }

  const params =
    [urlSearchParams1, urlSearchParams2, urlSearchParams3, urlSearchParams4]
      .find(p => p.toString()) ?? new URLSearchParams('')

  return sanitizeSearchParams(params)
}

export const getSanitizedMarketingSearchParamsString = () => {
  const sanitizedSearchParams = getSanitizedMarketingSearchParams()
  return sanitizedSearchParams.toString() ? `?${sanitizedSearchParams.toString()}` : ''
}

export const getMarketingAttributionParams = (): URLSearchParams => {
  const source = getSanitizedMarketingSearchParams()
  const attribution = new URLSearchParams()

  AD_PARAM_KEYS.forEach(key => {
    const value = source.get(key)
    if (value) attribution.set(key, value)
  })

  return attribution
}

// The current visit's non-sensitive URL params (secrets stripped) — what the
// URL-writers (sanitizeUrl, purchase-retry redirect) put in the address bar.
// Sources: INITIAL_SEARCH_PATH (survives onboarding's history.push) merged with
// the live URL. Deliberately NOT the all-time localStorage store: it persists
// across sessions, so a param-less return would resurrect a previous visit's
// click-ID and the Pixel would re-stamp a stale click with a fresh timestamp.
export const getCurrentVisitUrlParams = (): URLSearchParams => {
  const merged = new URLSearchParams(INITIAL_SEARCH_PATH ?? '')
  new URLSearchParams(window.location.search).forEach((value, key) => merged.set(key, value))
  return sanitizeSearchParams(merged)
}

export const getCurrentVisitUrlParamsString = (): string => {
  const params = getCurrentVisitUrlParams()
  return params.toString() ? `?${params.toString()}` : ''
}

// This visit's sanitized URL. Use where the URL states where an event happened,
// rather than getAnalyticsUrl, which can resolve a stored previous-visit click-ID.
export const getCurrentVisitUrl = (): string =>
  `${location.origin}${location.pathname}${getCurrentVisitUrlParamsString()}`

const saveSearchParams = () => {
  if (window.location.search === '') {
    return
  }

  // Merge into (not overwrite) the stored copy: delayed readers (resolvePurchaseSaga,
  // token login) recover params sanitizeUrl stripped from the URL. An overwrite would
  // wipe them on sanitizeUrl's re-run. removeSearchParams is the explicit cleanup path.
  const merged = new URLSearchParams(localStorage.getItem('searchParams') ?? '')
  new URLSearchParams(window.location.search).forEach((value, key) => merged.set(key, value))
  localStorage.setItem('searchParams', merged.toString())
}

export const sanitizeUrl = (history: H.History) => {
  saveSearchParams()

  if (location.pathname === RouteNames.HOME) {
    return
  }

  let modifiedUrlString = location.pathname
  if (modifiedUrlString.endsWith('/')) {
    modifiedUrlString = modifiedUrlString.slice(0, -1) // remove trailing slash
  }

  // Keep the current visit's non-sensitive params in the URL (recovered from
  // INITIAL_SEARCH_PATH across onboarding's history.push) so async ad pixels can
  // build their click cookies. Secrets are stripped and the all-time store is
  // never read, so a param-less return stays clean. See getCurrentVisitUrlParams.
  modifiedUrlString += getCurrentVisitUrlParamsString()

  // no-op if the URL is already in the desired state
  const originalUrl = location.pathname + location.search
  if (modifiedUrlString !== originalUrl) {
    history.replace(modifiedUrlString)
  }
}

export const getAnalyticsUrl = () => `${location.origin}${location.pathname}${getSanitizedMarketingSearchParamsString()}`

export const getAnalyticsPath = () => `${location.pathname}${getSanitizedMarketingSearchParamsString()}`

export const navigateToPaymentSuccess = async (history: ReturnType<typeof useHistory>, userTokenId: string, subscriptionId?: string, source?: 'stripe' | 'elements' | 'paypal', sendEvent: boolean = true) => {
  const searchParams = getSearchParamsString().replace('?', '')

  const params = qs.parse(searchParams)

  const search = qs.stringify({
    ...params,
    subscriptionId,
    token: userTokenId,
    source,
    send_event: sendEvent
  })

  const paymentSuccessPath = getPaymentSuccessUrl()

  history.replace(`${paymentSuccessPath}?${search}`)
}

export const removeSearchParams = (params: string[]) => {
  const urlSearchParams = getSearchParams()

  params.forEach(param => {
    localStorage.removeItem(param)
    urlSearchParams.delete(param)
  })

  const searchParamsString = urlSearchParams.toString()
  localStorage.setItem('searchParams', searchParamsString)

  // localStorage alone no longer removes a param — sanitizeUrl keeps non-sensitive
  // params in the URL and getSearchParams re-merges it on every read. Strip from the
  // URL too so the removal sticks (replaceState; these are read via getSearchParams,
  // not the router's Redux location, and clearAppDataSaga navigates right after).
  const urlParams = new URLSearchParams(window.location.search)
  const paramsInUrl = params.filter(param => urlParams.has(param))
  if (paramsInUrl.length > 0) {
    paramsInUrl.forEach(param => urlParams.delete(param))
    const remainingQuery = urlParams.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${remainingQuery ? `?${remainingQuery}` : ''}`)
  }
}

/**
 * We use this mainly for growthbook in order to run A/B test for the anonymous users since we can not get the pseudo id from firebase.
 * Also, we are adding this to Sentry and Analytics events too. So that, we can match the users in those platforms to detect the bugs/issues.
 */
export const getLocalId = (): string => {
  let localId = localStorage.getItem('localId')

  if (!localId) {
    localId = uuidv4()
    localStorage.setItem('localId', localId)
  }

  return localId
}

export const getFacebookEventId = (): string => uuidv4()

const META_PURCHASE_EVENT_ID_KEY = 'metaPurchaseEventId'

/**
 * Generates a fresh Purchase dedup id and stashes it in sessionStorage. Call
 * this when the user starts a checkout (Stripe session / subscription create
 * / PayPal etc.) so the same id can be:
 *   1. Persisted into the Stripe Subscription metadata (consumed by the
 *      server-side Meta CAPI Purchase from the Stripe webhook).
 *   2. Read back via `getMetaPurchaseEventId()` when the browser fires its
 *      own Pixel + Signals Gateway Purchase on the payment-success page.
 *
 * Using our own UUID instead of a Stripe-internal id (subscription / invoice
 * / payment_intent) avoids edge cases at trials ($0 invoices), unexpanded
 * fields, and keeps the dedup contract independent of payment provider.
 */
export const generateAndStoreMetaPurchaseEventId = (): string => {
  const id = uuidv4()
  try {
    sessionStorage.setItem(META_PURCHASE_EVENT_ID_KEY, id)
  } catch {
    // sessionStorage can throw in private-mode browsers — the id is still
    // returned so the caller can pass it to Stripe metadata; only browser-
    // side dedup is lost in that rare case.
  }
  return id
}

export const getMetaPurchaseEventId = (): string | null => {
  try {
    return sessionStorage.getItem(META_PURCHASE_EVENT_ID_KEY)
  } catch {
    return null
  }
}

export const openUrlInNewTab = (url: string) => {
  window.open(url, '_blank')
}

export const openEmailApp = (to: string, subject: string = '', body: string = '') => {
  window.open(`mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`)
}

export const getCurrency = () => {
  const searchParams = getSearchParams()
  if (searchParams) {
    const currency = searchParams.get('currency')
    const curr = searchParams.get('curr')

    return currency ?? curr ?? undefined
  }
}

// Read the Pixel-written click cookies for the server-side CAPI path. getCookiesMap
// matches the exact name — a regex like /_fbc=/ would also match a cookie merely
// ending in _fbc. The SDKs build these themselves from the click-IDs in the URL.
export function getFbc(): string | null {
  return getCookiesMap().get('_fbc') ?? null
}

export function getFbp(): string | null {
  return getCookiesMap().get('_fbp') ?? null
}
